import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { FileText, Download, Loader2, ArrowLeft, Eye, Search } from 'lucide-react';
import { Device } from '../../types/device';
import moment from 'moment';
import { GetEdgeLogDetails } from '../../../../redux/UserManagement/saga';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../../../components/ui/dialog';
import { Pagination } from '../../../../components/ui/Pagination';
import { AntdDate } from '../../../../components/ui/antd-date';

const PAGE_SIZE = 10;

type LogFileRow = { key: string; url: string; lastModified: string };

interface DeviceLogDetailsProps {
    device: Device;
}

export const DeviceLogDetails: React.FC<DeviceLogDetailsProps> = ({ device }) => {
    /** `count` holds total records for pagination (prefers API `totalCount`, else legacy `count` / client slice). */
    const [edgeLogData, setEdgeLogData] = useState<{
        fileUrls: LogFileRow[];
        count: number;
        message?: string;
    } | null>(null);
    const [edgeLogsLoading, setEdgeLogsLoading] = useState(false);
    const [edgeLogsError, setEdgeLogsError] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [searchDraft, setSearchDraft] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFromDraft, setDateFromDraft] = useState('');
    const [dateToDraft, setDateToDraft] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewModalFileName, setViewModalFileName] = useState('');
    const [viewModalContent, setViewModalContent] = useState<string | null>(null);
    const [viewModalLoading, setViewModalLoading] = useState(false);
    const [viewModalError, setViewModalError] = useState<string | null>(null);

    const getFileNameFromKey = (key: string) => {
        const parts = key.split('/');
        return parts[parts.length - 1] || key;
    };

    const handleViewLogFile = async (file: { key: string; url: string }) => {
        const fileName = getFileNameFromKey(file.key);
        setViewModalFileName(fileName);
        setViewModalOpen(true);
        setViewModalContent(null);
        setViewModalError(null);
        setViewModalLoading(true);
        try {
            const res = await fetch(file.url, { mode: 'cors' });
            if (!res.ok) throw new Error(`Failed to load file: ${res.status}`);
            const text = await res.text();
            setViewModalContent(text);
        } catch (err) {
            setViewModalError(err instanceof Error ? err.message : 'Failed to load log file contents.');
        } finally {
            setViewModalLoading(false);
        }
    };

    const deviceType = device.deviceData?.process_type === 'coating' ? 'Coating' : '';

    const fetchEdgeLogs = useCallback(async () => {
        if (!device?.id) return;
        setEdgeLogsLoading(true);
        setEdgeLogsError(null);
        try {
            const offset = (currentPage - 1) * PAGE_SIZE;
            const data = await GetEdgeLogDetails({
                deviceId: device.id,
                deviceType,
                limit: PAGE_SIZE,
                offset,
                search: searchQuery.trim() || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                sortOrder: 'DESC',
            });

            if (data?.fileUrls) {
                const d = data as {
                    fileUrls: LogFileRow[];
                    count?: number;
                    totalCount?: number;
                    message?: string;
                };
                const raw = Array.isArray(d.fileUrls) ? d.fileUrls : [];
                const sorted = [...raw].sort((a, b) => {
                    const ta = a.lastModified ? moment(a.lastModified).valueOf() : 0;
                    const tb = b.lastModified ? moment(b.lastModified).valueOf() : 0;
                    return tb - ta;
                });

                const apiTotalCount =
                    typeof d.totalCount === 'number' && !Number.isNaN(d.totalCount) ? d.totalCount : null;

                let pageFiles = sorted;
                let totalForPagination: number;

                if (apiTotalCount != null) {
                    totalForPagination = apiTotalCount;
                } else if (sorted.length > PAGE_SIZE) {
                    totalForPagination = sorted.length;
                    pageFiles = sorted.slice(offset, offset + PAGE_SIZE);
                } else {
                    totalForPagination =
                        typeof d.count === 'number' && !Number.isNaN(d.count) ? d.count : sorted.length;
                }

                setEdgeLogData({
                    fileUrls: pageFiles,
                    count: totalForPagination,
                    message: d.message,
                });
            } else {
                setEdgeLogData(null);
                setEdgeLogsError(data ? 'No log files returned.' : 'Failed to load log files.');
            }
        } catch {
            setEdgeLogData(null);
            setEdgeLogsError('Failed to load log files.');
        } finally {
            setEdgeLogsLoading(false);
        }
    }, [device?.id, deviceType, currentPage, searchQuery, dateFrom, dateTo]);

    useEffect(() => {
        if (device?.id) {
            void fetchEdgeLogs();
        }
    }, [device?.id, fetchEdgeLogs]);

    const applyFilters = () => {
        setSearchQuery(searchDraft.trim());
        setDateFrom(dateFromDraft);
        setDateTo(dateToDraft);
        setCurrentPage(1);
    };

    const totalItems = edgeLogData?.count ?? 0;

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-2 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-blueprint-blue">Device Logs</h3>
                <Button onClick={() => void fetchEdgeLogs()} disabled={edgeLogsLoading}>
                    {edgeLogsLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading…
                        </>
                    ) : (
                        <div className="flex items-center whitespace-nowrap justify-center gap-2">
                            <FileText className="h-4 w-4" />
                            Refresh Log Files
                        </div>
                    )}
                </Button>
            </div>
            <div className="p-6 flex flex-col h-full">
                {edgeLogsError && (
                    <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                        {edgeLogsError}
                    </div>
                )}

                {device?.id && (
                    <div className="mb-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50/80 p-4 sm:flex-row sm:flex-wrap sm:items-end">
                        <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
                            <label className="text-xs font-medium text-gray-600" htmlFor="device-log-search">
                                Search file name
                            </label>
                            <div className="flex gap-2">
                                <Input
                                    id="device-log-search"
                                    placeholder="Filter by name…"
                                    value={searchDraft}
                                    onChange={(e) => setSearchDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') applyFilters();
                                    }}
                                    className="h-9 bg-white"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5 sm:w-40">
                            <span className="text-xs font-medium text-gray-600">From date</span>
                            <AntdDate
                                value={dateFromDraft}
                                onChange={setDateFromDraft}
                                placeholder="From"
                                className="h-9 w-full"
                                size="small"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5 sm:w-40">
                            <span className="text-xs font-medium text-gray-600">To date</span>
                            <AntdDate
                                value={dateToDraft}
                                onChange={setDateToDraft}
                                placeholder="To"
                                className="h-9 w-full"
                                size="small"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" size="sm" className="h-9" onClick={applyFilters}>
                                <Search className="mr-1.5 h-4 w-4" />
                                Apply
                            </Button>
                            <Button
                                type="button"
                               // variant="outline"
                                size="sm"
                                className="h-9"
                                onClick={() => {
                                    setSearchDraft('');
                                    setSearchQuery('');
                                    setDateFromDraft('');
                                    setDateToDraft('');
                                    setDateFrom('');
                                    setDateTo('');
                                    setCurrentPage(1);
                                }}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                )}

                {edgeLogsLoading && (
                    <div className="flex flex-col items-center justify-center py-16 flex-1">
                        <Loader2 className="h-10 w-10 animate-spin text-gray-400 mb-4" />
                        <p className="text-sm font-medium text-gray-700">Loading log files…</p>
                        <p className="text-xs text-gray-500 mt-1">Fetching device log list</p>
                    </div>
                )}
                {!edgeLogData && !edgeLogsLoading && !edgeLogsError && (
                    <div className="text-center py-12 flex-1">
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">System Logs</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            View device logs and system events. Click &quot;Refresh Log Files&quot; to load available log files.
                        </p>
                    </div>
                )}
                {edgeLogData && edgeLogData.fileUrls.length === 0 && !edgeLogsLoading && (
                    <div className="text-center py-12 flex-1">
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No log files match the current filters.</p>
                    </div>
                )}
                {edgeLogData && edgeLogData.fileUrls.length > 0 && !edgeLogsLoading && (
                    <>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>File Name</TableHead>
                                        <TableHead>Last Modified</TableHead>
                                        <TableHead className="text-center">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {edgeLogData.fileUrls.map((file) => {
                                        const fileName = getFileNameFromKey(file.key);
                                        const lastModified = file.lastModified
                                            ? moment(file.lastModified).format('DD-MMM-YYYY HH:mm')
                                            : '—';
                                        return (
                                            <TableRow key={file.key}>
                                                <TableCell className="font-medium">{fileName}</TableCell>
                                                <TableCell className="text-gray-500">{lastModified}</TableCell>
                                                <TableCell className="text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleViewLogFile(file)}
                                                            title="View"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            title="Download"
                                                            onClick={() => {
                                                                const a = document.createElement('a');
                                                                a.href = file.url;
                                                                a.download = fileName;
                                                                a.rel = 'noopener noreferrer';
                                                                a.target = '_blank';
                                                                document.body.appendChild(a);
                                                                a.click();
                                                                document.body.removeChild(a);
                                                            }}
                                                        >
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalItems={totalItems}
                            itemsPerPage={PAGE_SIZE}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={() => {}}
                            pageSizeOptions={[PAGE_SIZE]}
                        />
                    </>
                )}
            </div>

            <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
                <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden flex flex-col bg-page-bg rounded-none border-0 shadow-none">
                    <div className="flex flex-col relative z-20 h-screen overflow-hidden">
                        <DialogHeader className="px-4 sm:px-6 py-4 pr-14 border-b border-[#E2E5EA] flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => setViewModalOpen(false)}
                                    className="h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                                    variant="outline"
                                    disabled={viewModalLoading}
                                >
                                    <ArrowLeft className="h-4 w-4 text-text-grey" />
                                </Button>
                                <div className="flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-heritage-grey" />
                                    <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                                        {viewModalFileName || 'Log file'}
                                        {viewModalLoading && (
                                            <span className="ml-2 text-[13px] font-normal text-heritage-grey">(Loading…)</span>
                                        )}
                                    </DialogTitle>
                                </div>
                            </div>
                        </DialogHeader>

                        <div className="p-4 sm:p-6 overflow-y-auto flex-1 relative bg-page-bg">
                            {viewModalLoading && (
                                <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg">
                                    <Loader2 className="h-8 w-8 animate-spin text-hero-red mb-4" />
                                    <span className="text-[13px] text-text-grey">Loading log file…</span>
                                </div>
                            )}
                            {viewModalError && !viewModalLoading && (
                                <div className="p-4 text-[13px] text-readable-red bg-[rgba(196,0,31,0.06)] rounded-lg border border-hero-red-20">
                                    {viewModalError}
                                </div>
                            )}
                            {viewModalContent != null && !viewModalLoading && (
                                <pre className="p-4 text-[12px] text-text-grey whitespace-pre-wrap font-mono text-left bg-white rounded-lg border border-[#E2E5EA] min-h-full">
                                    {viewModalContent}
                                </pre>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
